<template>
  <div class="control is-horizontal recipe-preorder-times-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-RECIPE_PREORDER_TIMES"></label-name>
    </div>

    <div class="control withInfoText recipe-preorder-container">
      <div class="preorder-times-stacked">
        <!-- 1. ORDER UNTIL -->
        <div class="preorder-row">
          <span class="preorder-label">Vorbestellung bis</span>
          <div class="select">
            <select v-model="internalData.orderUntil" @change="emitUpdate">
              <option :value="null">--:--</option>
              <option v-for="t in timeOptions" :key="'order-' + t.value" :value="t.value">
                {{ t.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- 2. PICKUP FROM -->
        <div class="preorder-row">
          <span class="preorder-label">Abholbereit ab</span>
          <div class="select">
            <select v-model="internalData.pickUpFrom" @change="emitUpdate">
              <option :value="null">--:--</option>
              <option v-for="t in timeOptions" :key="'pickup-' + t.value" :value="t.value">
                {{ t.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- 3. NOTIFY UNTIL -->
        <div class="preorder-row">
          <span class="preorder-label">Benachrichtigung bis</span>
          <div class="select">
            <select v-model="internalData.notifyUntil" @change="emitUpdate">
              <option :value="null">--:--</option>
              <option v-for="t in timeOptions" :key="'notify-' + t.value" :value="t.value">
                {{ t.name }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <span class="help is-info-text">
        Fristen und Benachrichtigungszeiten für die Rezept-Vorbestellung in der Kunden-App
      </span>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeData()"
        title="JSON-Rohdaten (Vorbestellungs-Zeiten)"
        :rows="5"
        placeholder="JSON hier einfügen..."
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';
function generateBaseTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h++) {
    const hh = String(h).padStart(2, '0');
    for (const m of ['00', '15', '30', '45']) {
      options.push({
        value: `${hh}:${m}`,
        name: `${hh}:${m}`
      });
    }
  }
  return options;
}

const BASE_TIME_OPTIONS = generateBaseTimeOptions();

export default {
  name: 'RecipePreorderTimes',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Expects object { orderUntil: "11:30", pickUpFrom: "16:30", notifyUntil: "18:00" }
    // Or a JSON string that will be parsed.
    value: {
      type: [Object, String],
      default: () => ({})
    }
  },

  data() {
    return {
      internalData: this.parseValue(this.value),
      isInternalUpdate: false
    };
  },

  computed: {
    timeOptions() {
      const options = [...BASE_TIME_OPTIONS];
      const customTimes = [
        this.internalData.orderUntil,
        this.internalData.pickUpFrom,
        this.internalData.notifyUntil
      ];

      customTimes.forEach(t => {
        if (t && typeof t === 'string' && !options.some(o => o.value === t)) {
          options.push({ value: t, name: t });
        }
      });

      return options.sort((a, b) => a.value.localeCompare(b.value));
    }
  },

  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.internalData = this.parseValue(newVal);
      }
    }
  },

  methods: {
    parseValue(val) {
      let data = val;
      if (typeof val === 'string') {
        try {
          data = JSON.parse(val);
        } catch (e) {
          data = {};
        }
      }
      if (!data || typeof data !== 'object') {
        data = {};
      }

      return {
        orderUntil: data.orderUntil || null,
        pickUpFrom: data.pickUpFrom || null,
        notifyUntil: data.notifyUntil || null
      };
    },

    serializeData() {
      return {
        orderUntil: this.internalData.orderUntil || null,
        pickUpFrom: this.internalData.pickUpFrom || null,
        notifyUntil: this.internalData.notifyUntil || null
      };
    },

    emitUpdate() {
      const payload = this.serializeData();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);
    },

    onApplyRawJson(parsed, done) {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return done('Das JSON muss ein Objekt mit den Feldern orderUntil, pickUpFrom und notifyUntil sein.');
      }
      this.internalData = this.parseValue(parsed);
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.recipe-preorder-times-control {
  margin-top: 0;
  margin-bottom: 14px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.recipe-preorder-container {
  flex: 1;
  max-width: 880px;
  width: 100%;
}

.preorder-times-stacked {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .preorder-row {
    display: flex;
    align-items: center;

    .preorder-label {
      font-size: 13px;
      color: #4a4a4a;
      min-width: 145px;
      width: 145px;
    }

    .select {
      height: 32px;

      select {
        height: 32px;
        font-size: 13px;
        width: 100px;
        min-width: 100px;
        max-width: 100px;
        padding-left: 10px;
        padding-right: 28px;
        border-radius: 3px;
        border-color: #dbdbdb;
        background-color: #fff;

        &:focus {
          border-color: #45a3d2;
        }
      }

      &::after {
        right: 12px;
      }
    }
  }
}

.help.is-info-text {
  display: block;
  font-size: 11px;
  color: #7a7a7a;
  margin-top: 8px;
  margin-bottom: 0;
  line-height: 1.4;
}

</style>
